import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Permission, User, UiWidgets, WidgetType } from '@/interfaces/user';
import userServices from '@/api/userServices';

interface UserState {
  currentUser: User;
  permissions: string[];
  fullPermissions: Permission[];
  userSettings: { uiWidgets: UiWidgets; autoStatementGenerationEnabled: boolean };
  cbsStatus: string;
  loading: boolean;
  error: string;
  billingError: string | null;
  widgets: WidgetType[];
}

const initialState: UserState = {
  currentUser: {
    id: '',
    firstName: '',
    lastName: '',
    username: '',
    phone: '',
    email: '',
    isSubUser: true,
    language: '',
    isMain: false,
    subscriptionEndDate: '',
    duePaymentDate: '',
    mfaEnabled: false,
    mfaType: undefined,
    isFirstPasswordChanged: false,
    subscriptionInfo: {
      billingAccountNumber: '',
      isAutoCharging: false,
    },
    permanentRole: '',
    packageInfo: {
      description: '',
      title: '',
      freeOfCharge: false,
      maxNumberSubUsers: 0,
      periodicity: '',
      permissions: [],
      pricing: [],
    },
    validatorsCount: 0,
    unreadAlertsCount: 0,
  },
  cbsStatus: 'UP',
  permissions: [],
  userSettings: {
    uiWidgets: {},
    autoStatementGenerationEnabled: false,
  },
  fullPermissions: [],
  loading: false,
  error: '',
  billingError: null,
  widgets: [],
};

export const fetchUser = createAsyncThunk('users/fetch', async () => {
  return userServices.me();
});

export const fetchCBSStatus = createAsyncThunk('users/fetchCBSStatus', async () => {
  return userServices.getCBSStatus();
});

export const fetchSettings = createAsyncThunk('users/fetchSettings', async () => {
  return userServices.getUserSettings();
});

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
    },
    logoutUser: () => initialState,
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.currentUser) state.currentUser = action.payload as User;
    },
    toggleMfa: state => {
      state.currentUser.mfaEnabled = !state.currentUser.mfaEnabled;
    },
    setCheckCBS: (state, action) => {
      state.cbsStatus = action.payload;
    },
    updateSettings: (state, action: PayloadAction<any>) => {
      state.userSettings = action.payload;
    },
    decreaseUnreadAlertsCount: (state, action: PayloadAction<{ isReadAll?: boolean }>) => {
      if (action.payload.isReadAll) {
        state.currentUser.unreadAlertsCount = 0;
      } else {
        state.currentUser.unreadAlertsCount -= 1;
      }
    },
    increaseUnreadAlertsCount: state => {
      state.currentUser.unreadAlertsCount += 1;
    },
    setWidgets: (state, action: PayloadAction<WidgetType[]>) => {
      state.widgets = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchUser.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchUser.fulfilled, (state, action) => {
      const { payload } = action;

      state.currentUser = payload;
      state.fullPermissions = payload.permissions;
      state.permissions = payload.permissions.map((perm: Permission) => perm.name);
      state.billingError = payload.billingError ?? null;
      state.loading = false;
    });

    builder.addCase(fetchUser.rejected, (state, action) => {
      state.loading = false;

      if (action.error.code === 'bill_not_found') {
        state.currentUser = initialState.currentUser;
        state.billingError = 'bill_not_found';
      } else {
        state.error = 'Error';
      }
    });

    builder.addCase(fetchCBSStatus.fulfilled, (state, action) => {
      const { payload } = action;
      state.cbsStatus = payload.status;
    });

    builder.addCase(fetchSettings.fulfilled, (state, action) => {
      const { settings } = action.payload;
      const uiWidgets: UiWidgets = settings.uiWidgets ?? {};
      const autoStatementGenerationEnabled: boolean = settings.autoStatementGenerationEnabled ?? false;
      state.userSettings = { ...settings, uiWidgets, autoStatementGenerationEnabled };
      const WIDGET_IDS = ['transactionsHistory', 'ibTransactions', 'recentPayments', 'beneficiaries'];
      const hasOrder = Object.values(uiWidgets).some(w => w?.order !== undefined);
      let widgets: WidgetType[];
      if (hasOrder) {
        widgets = WIDGET_IDS.map(id => ({ id, checked: uiWidgets[id]?.enabled ?? false, order: uiWidgets[id]?.order ?? 99 }))
          .sort((a, b) => a.order - b.order)
          .map(({ id, checked }) => ({ id, checked }));
      } else {
        widgets = WIDGET_IDS.map(id => ({ id, checked: uiWidgets[id]?.enabled ?? false }));
      }
      state.widgets = widgets;
    });
  },
});

export const {
  setCurrentUser,
  logoutUser,
  updateUser,
  toggleMfa,
  setCheckCBS,
  decreaseUnreadAlertsCount,
  increaseUnreadAlertsCount,
  updateSettings,
  setWidgets,
} = usersSlice.actions;

export const selectUsers = (state: RootState) => state.users;

export default usersSlice.reducer;
